import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ThumbsUp, ThumbsDown, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import './TrackFeedback.css';

const LIKE_REASONS = [
  "Right vibe for what I'm doing",
  'Love this song',
  'Love this artist',
  'Love this genre',
  'Wow moment',
];

const DISLIKE_REASONS = [
  'Wrong vibe for the moment',
  'Like the song, not for now',
  "Don't like this song",
  "Don't like this artist",
  "Don't like this genre",
];

const AUTO_DISMISS_MS = 6000;

export default function TrackFeedback({
  currentlyPlaying,
  sessionId,
  playlistId,
  subVibe,
  isPlaying,
  apiBaseUrl,
  trackProgress,
  trackDuration,
  onDislike,
  onRegenerated,
  action,
  children,
}) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);
  const [likedActive, setLikedActive] = useState(false);
  const [timerKey, setTimerKey] = useState(0);

  const submitLock = useRef(false);
  const timerRef = useRef(null);
  const snapshotRef = useRef(null);
  const closeSheetRef = useRef(null);

  const disabled = !currentlyPlaying || !isPlaying;

  const getTrackData = useCallback(() => {
    if (snapshotRef.current) return snapshotRef.current;
    return {
      songName: currentlyPlaying?.title || currentlyPlaying?.name,
      artist: currentlyPlaying?.artist || currentlyPlaying?.artists?.[0]?.name,
      position: trackProgress ?? null,
      duration: trackDuration ?? null,
      spotifyTrackId: currentlyPlaying?.spotifyTrackId || null,
    };
  }, [currentlyPlaying, trackProgress, trackDuration]);

  const closeSheet = useCallback((skipFallback = false) => {
    // If the overlay is closing without a reason selected, still log the
    // thumb-up/down event with empty reasons — preserves the polarity signal
    // (honest unknown), distinct from a deliberate chip pick.
    if (!skipFallback && sheetOpen && feedbackType && !selectedReason && !submitLock.current) {
      submitLock.current = true;
      setSubmitting(true);
      const userEmail = localStorage.getItem('user_email');
      const track = getTrackData();
      axios.post(`${apiBaseUrl}/submit-track-feedback`, {
        email: userEmail,
        sessionId,
        feedbackType,
        songName: track.songName,
        artist: track.artist,
        playlistId,
        subVibe: subVibe ?? null,
        reasons: [],
        position: track.position,
        duration: track.duration,
        action: action || '',
        spotifyTrackId: track.spotifyTrackId || '',
        origin: 'dj_app',
      }).then((response) => {
        if (onRegenerated && response?.data?.regeneratedPlaylistId) {
          onRegenerated(response.data);
        }
      }).catch(err => {
        console.error('[TrackFeedback] Error submitting fallback:', err);
      }).finally(() => {
        setSubmitting(false);
        submitLock.current = false;
      });
    }

    snapshotRef.current = null;
    setSheetOpen(false);
    setFeedbackType(null);
    setSelectedReason(null);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [sheetOpen, feedbackType, selectedReason, apiBaseUrl, sessionId, playlistId, subVibe, getTrackData, action, onRegenerated]);

  // Keep a ref to the latest closeSheet so startTimer doesn't depend on it
  useEffect(() => {
    closeSheetRef.current = closeSheet;
  }, [closeSheet]);

  const startTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setTimerKey((k) => k + 1);
    timerRef.current = setTimeout(() => {
      closeSheetRef.current();
    }, AUTO_DISMISS_MS);
  }, []);

  // Start auto-dismiss timer when overlay opens
  useEffect(() => {
    if (sheetOpen) {
      startTimer();
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sheetOpen, startTimer]);

  const handleThumbClick = (type) => {
    if (disabled) return;
    setFeedbackType(type);
    setSheetOpen(true);

    if (type === 'like') {
      setLikedActive(true);
      setTimeout(() => setLikedActive(false), 600);
    }

    if (type === 'dislike') {
      snapshotRef.current = {
        songName: currentlyPlaying?.title || currentlyPlaying?.name,
        artist: currentlyPlaying?.artist || currentlyPlaying?.artists?.[0]?.name,
        position: trackProgress ?? null,
        duration: trackDuration ?? null,
        spotifyTrackId: currentlyPlaying?.spotifyTrackId || null,
      };
    }
  };

  const submitFeedback = async (reasons) => {
    if (submitLock.current) return;
    submitLock.current = true;
    setSubmitting(true);

    const userEmail = localStorage.getItem('user_email');
    const track = getTrackData();
    const submittedFeedbackType = feedbackType;
    const submittedAction = action;

    // Close the sheet immediately — /submit-track-feedback runs the GPT-based
    // feedback agent and can take several seconds, so waiting on it before
    // closing left the modal stuck on screen until the 6 s timer expired (DJ-130).
    closeSheet(true);

    // Skip the track now that the user picked a reason. We deliberately do
    // NOT skip on the initial 👎 click — the song keeps playing while the
    // user decides why they're disliking it (DJ-130).
    if (submittedFeedbackType === 'dislike' && onDislike) {
      onDislike();
    }

    try {
      const fbResp = await axios.post(`${apiBaseUrl}/submit-track-feedback`, {
        email: userEmail,
        sessionId,
        feedbackType: submittedFeedbackType,
        songName: track.songName,
        artist: track.artist,
        playlistId,
        subVibe: subVibe ?? null,
        reasons,
        position: track.position,
        duration: track.duration,
        action: submittedAction || '',
        spotifyTrackId: track.spotifyTrackId || '',
        origin: 'dj_app',
      });
      if (onRegenerated && fbResp?.data?.regeneratedPlaylistId) {
        onRegenerated(fbResp.data);
      }

      const actionSuffix = submittedAction && submittedAction !== 'No Music' ? ` when you're in ${submittedAction} mode` : '';
      toast.success(
        submittedFeedbackType === 'like'
          ? `Got it! I'll play more like this${actionSuffix}`
          : `Noted! I'll avoid songs like this${actionSuffix}`,
        { autoClose: 2000, position: 'bottom-center' }
      );
    } catch (err) {
      console.error('[TrackFeedback] Error submitting:', err);
      toast.error('Failed to submit feedback', {
        autoClose: 2500,
        position: 'bottom-center',
      });
    } finally {
      setSubmitting(false);
      submitLock.current = false;
    }
  };

  const handleChipClick = (reason) => {
    if (submitting) return;
    startTimer();
    setSelectedReason(reason);
    submitFeedback([reason]);
  };

  const reasons = feedbackType === 'like' ? LIKE_REASONS : DISLIKE_REASONS;
  const prompt =
    feedbackType === 'like'
      ? 'What made this track special? \u2728'
      : 'Help us understand what went wrong \uD83C\uDFAF';

  const displayTrack = getTrackData();

  return (
    <>
      <button
        className="track-feedback-btn track-feedback-btn--down"
        onClick={() => handleThumbClick('dislike')}
        disabled={disabled}
        title="Dislike this track"
      >
        <ThumbsDown size={18} />
      </button>

      {children}

      <button
        className={`track-feedback-btn track-feedback-btn--up${likedActive ? ' track-feedback-btn--glow' : ''}`}
        onClick={() => handleThumbClick('like')}
        disabled={disabled}
        title="Like this track"
      >
        <ThumbsUp size={18} />
      </button>

      {sheetOpen && (
        <div className={`track-overlay track-overlay--${feedbackType}`}>
          <div className="track-overlay-header">
            <span className="track-overlay-prompt">{prompt}</span>
            <button className="track-overlay-close" onClick={closeSheet}>
              <X size={18} />
            </button>
          </div>

          <div className="track-overlay-track">
            {displayTrack.songName} —{' '}
            {displayTrack.artist}
          </div>

          <div className="track-overlay-chips">
            {reasons.map((reason) => (
              <button
                key={reason}
                className={`track-overlay-chip${selectedReason === reason ? ' track-overlay-chip--selected' : ''}${reason === 'Wow moment' ? ' track-overlay-chip--wow' : ''}`}
                onClick={() => handleChipClick(reason)}
                disabled={submitting}
              >
                {reason}
              </button>
            ))}
          </div>

          <div className="track-overlay-timer">
            <div key={timerKey} className="track-overlay-timer-bar" />
          </div>
        </div>
      )}
    </>
  );
}
